import { SearchInput } from "PFApp/components/search_input";
import { useGrowl } from "PFApp/use_growl";
import ConfirmModal, { ConfirmModalHeader } from "PFComponents/confirm/confirm_modal";
import { Tab, Tabs, TabVariant } from "PFComponents/tabs";
import { Typography } from "PFComponents/typography";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { useSkillsFrameworkDelete } from "PFCore/hooks/queries/admin/skills_frameworks/use_skills_framework_delete";
import { useCustomValuesInvalidate } from "PFCore/hooks/queries/custom_fields/admin";
import { SkillsFrameworkRanks } from "PFTypes";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { AdminItemPage } from "../../components/admin_item_page";
import { CreateNewFrameworkButton } from "../components/create_new_framework_button";
import { ExportFrameworksButton } from "../components/export_frameworks_button";
import { SkillsFrameworksEmptyPage } from "./empty_page";
import { useSkillsFrameworksCounters } from "./hooks/use_skills_frameworks_counters";
import { useSkillsFrameworksListPage } from "./hooks/use_skills_frameworks_list_page";
import { useSkillsFrameworksTab } from "./hooks/use_skills_frameworks_tab";
import css from "./skills_frameworks_list_page.module.scss";
import { SkillsFrameworksTable } from "./table";

export const SkillsFrameworksListPage = () => {
  const { t } = useTranslation(["skillsFrameworks", "translation"]);
  const growl = useGrowl();
  const { getFormattedErrors } = useResponseErrors();
  const {
    isFetching,
    columns,
    skillsFrameworks,
    frameworkToDelete,
    setOrder,
    setFrameworkToDelete,
    term,
    setTerm
  } = useSkillsFrameworksListPage();

  const counters = useSkillsFrameworksCounters();

  const { activeTab, changeTab, setPage } = useSkillsFrameworksTab();

  const tabs: Tab[] = useMemo(
    () =>
      SkillsFrameworkRanks.map((item) => ({
        id: item,
        title: t(`skillsFrameworks:tabs.${item}`),
        count: counters[item]
      })),
    [counters, t]
  );

  const { mutateAsync: deleteSkillsFramework } = useSkillsFrameworkDelete();
  const [hasActivitiesConnected, setHasActivitiesConnected] = useState(false);
  const { invalidate: invalidateCustomValues } = useCustomValuesInvalidate();

  const handleEmptyPage = () => {
    if (skillsFrameworks && skillsFrameworks.entries.length === 1 && skillsFrameworks.meta.page > 1) {
      setPage(skillsFrameworks.meta.page - 1);
    }
  };

  const handleFrameworkRemove = async () => {
    try {
      await deleteSkillsFramework({ id: frameworkToDelete?.id, force: hasActivitiesConnected });
      setFrameworkToDelete(null);

      handleEmptyPage();
      invalidateCustomValues();

      growl({
        message: t("skillsFrameworks:list.table.actions.delete.successMessage"),
        kind: "success"
      });
    } catch ({ response }) {
      const errors = getFormattedErrors(response);
      if (errors && Object.keys(errors).includes("framework_activities")) {
        setHasActivitiesConnected(true);
      } else {
        growl({
          message: t("skillsFrameworks:list.table.actions.delete.errorMessage"),
          kind: "error"
        });
        setFrameworkToDelete(null);
      }
    }
  };

  const hasAnyFrameworks = (skillsFrameworks?.entries.length || 0) > 0;

  return (
    <AdminItemPage
      title={t("skillsFrameworks:title")}
      actions={
        <div className={css.actions}>
          {hasAnyFrameworks && <ExportFrameworksButton />}
          <CreateNewFrameworkButton />
        </div>
      }
      filtersSection={
        <div className={css.filtersSection}>
          <Tabs tabs={tabs} variant={TabVariant.Horizontal} onChange={changeTab} activeTab={activeTab} />
          <div className={css.filters}>
            <SearchInput term={term} onTermChange={setTerm} />
            {skillsFrameworks && (
              <Typography variant="bodyBold" noMargin className={css.count}>
                {t("translation:filters.countInTotal", { count: skillsFrameworks.meta.total })}
              </Typography>
            )}
          </div>
        </div>
      }
    >
      {hasAnyFrameworks ? (
        <SkillsFrameworksTable
          setFrameworkToDelete={setFrameworkToDelete}
          columns={columns}
          setOrder={setOrder}
          skillsFrameworks={skillsFrameworks!}
          isFetching={isFetching}
        />
      ) : (
        <SkillsFrameworksEmptyPage />
      )}
      {frameworkToDelete && (
        <ConfirmModal
          labelOK={t("translation:delete")}
          kindOK="danger"
          handleOK={handleFrameworkRemove}
          handleClose={() => setFrameworkToDelete(null)}
          handleCancel={() => setFrameworkToDelete(null)}
          autoHandleClose={false}
        >
          <ConfirmModalHeader className={css.modalHeader}>
            {t("skillsFrameworks:list.table.actions.delete.modal.header")}
          </ConfirmModalHeader>
          <div className={css.modalContent}>
            {t(
              `skillsFrameworks:list.table.actions.delete.modal.${
                hasActivitiesConnected ? "messageLinkedActivities" : "message"
              }`,
              { skillFrameworkName: frameworkToDelete.name }
            )}
          </div>
        </ConfirmModal>
      )}
    </AdminItemPage>
  );
};
